package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import com.qrkontaq.data.dto.WebAnalyticsDto
import com.qrkontaq.data.dto.WebDayStatDto
import com.qrkontaq.data.dto.WebNameCountDto
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import kotlin.js.Date
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.px

private const val EMERALD = "#34D399"
private const val AMBER = "#E8A33D"

/**
 * TRAFFIC section: web KPIs, a smoothed two-series area chart, and ranked lists
 * for pages, clicks, landing sections and language switches.
 */
@Composable
internal fun PlatformTrafficView(
    data: WebAnalyticsDto,
    days: Int,
) {
    Row(Modifier.fillMaxWidth().flexWrap(FlexWrap.Wrap).gap(12.px).margin(bottom = 12.px)) {
        QpKpi(AdminIcons.Users, PlatformT.anaVisitors, "${data.uniqueVisitors}", EMERALD)
        QpKpi(AdminIcons.Footprints, PlatformT.anaVisits, "${data.sessions}", AMBER)
        QpKpi(AdminIcons.Eye, PlatformT.anaPageviews, "${data.pageviews}", EMERALD)
        QpKpi(AdminIcons.LocateFixed, PlatformT.anaClicks, "${data.clicks}", "#8699BE")
    }
    Row(Modifier.fillMaxWidth().flexWrap(FlexWrap.Wrap).gap(12.px).margin(bottom = 14.px)) {
        QpKpi(AdminIcons.Sparkles, PlatformT.anaNewVisitors, "${data.newVisitors}", EMERALD)
        QpKpi(AdminIcons.Clock, PlatformT.anaAvgSession, formatDuration(data.avgSessionSeconds), AMBER)
        QpKpi(AdminIcons.Globe, PlatformT.anaOnline, "${data.activeNow}", EMERALD, live = data.activeNow > 0)
    }

    QpCard {
        QpTitle(PlatformT.anaChartTraffic)
        val filled = fillDays(data.byDay, days)
        QpAreaChart(
            labels = filled.map { it.date.takeLast(2) },
            series = listOf(
                AreaSeries(PlatformT.anaLegendVisitors, EMERALD, filled.map { it.visitors.toDouble() }),
                AreaSeries(PlatformT.anaLegendPageviews, AMBER, filled.map { it.pageviews.toDouble() }),
            ),
        )
    }

    QpTwoColumn(
        first = {
            QpCard {
                QpTitle(PlatformT.anaTopPages)
                QpRankedList(
                    data.topPages.map { QpRank(it.path, it.views, "${it.visitors}") },
                    trailingHint = "${PlatformT.anaColViews} · ${PlatformT.anaColVisitors}",
                )
            }
        },
        second = {
            QpCard {
                QpTitle(PlatformT.anaTopClicks)
                QpRankedList(data.topClicks.map { QpRank(it.name, it.count) }, trailingHint = PlatformT.anaColClicks)
            }
        },
    )

    QpTwoColumn(
        first = {
            QpCard {
                QpTitle(PlatformT.anaSections)
                QpRankedList(sortSections(data.sections).map { QpRank(it.name, it.count) }, trailingHint = PlatformT.anaSectionsHint)
            }
        },
        second = {
            QpCard {
                QpTitle(PlatformT.anaLangSwitches)
                QpRankedList(data.langSwitches.map { QpRank(it.name.replace(">", " → "), it.count) })
            }
        },
    )
}

/** Landing DOM order so the section list reads as a scroll funnel, not a rating. */
private val SECTION_ORDER = listOf("how", "features", "reviews", "pricing", "faq")

internal fun sortSections(items: List<WebNameCountDto>): List<WebNameCountDto> =
    items.sortedBy { SECTION_ORDER.indexOf(it.name).let { i -> if (i == -1) SECTION_ORDER.size else i } }

/** Fills the whole period with zero days so chart gaps don't lie. */
internal fun fillDays(
    byDay: List<WebDayStatDto>,
    days: Int,
): List<WebDayStatDto> {
    val known = byDay.associateBy { it.date }
    val dayMs = 24.0 * 3600 * 1000
    val now = Date.now()
    return (days - 1 downTo 0).map { offset ->
        val iso = Date(now - offset * dayMs).toISOString().take(10)
        known[iso] ?: WebDayStatDto(date = iso)
    }
}
